import { type FC } from 'react';
import { CornerDownLeft, Pencil, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type DraftReplyBarProps = {
  /** The saved draft text to preview. */
  text: string;
  /** Called when the user clicks the bar to load the draft back into the input. */
  onResume: () => void;
  /** Called when the user dismisses the draft via the close button. */
  onDiscard: () => void;
};

/**
 * Compact preview bar shown directly above the message composer when an unsent
 * draft exists. Clicking the bar resumes editing the draft; the close button
 * discards it. The "Click to resume" hint stays visible, as it does on mobile:
 * a desktop user who never hovered the bar could not tell it was actionable,
 * and lost the parked text as a result (IP-2191).
 */
export const DraftReplyBar: FC<DraftReplyBarProps> = ({ text, onResume, onDiscard }) => {
  const { t } = useTranslation();

  return (
    <div
      data-testid="draft-reply-bar"
      className="mx-2 mb-2 flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2 transition-colors hover:border-primary/40 hover:bg-primary/5 focus-within:border-primary/40 focus-within:bg-primary/5"
    >
      <button
        type="button"
        onClick={onResume}
        aria-label={t('chat_page.draftReplyResumeAriaLabel')}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Pencil className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-xs font-bold uppercase tracking-wide text-primary">
            {t('chat_page.draftReplyLabel')}
          </span>
          <span className="truncate text-sm text-foreground">{text}</span>
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1 pl-3 text-xs font-medium text-primary">
          <CornerDownLeft className="h-3.5 w-3.5" />
          {t('chat_page.draftReplyResume')}
        </span>
      </button>
      <button
        type="button"
        onClick={onDiscard}
        aria-label={t('chat_page.draftReplyDiscard')}
        title={t('chat_page.draftReplyDiscard')}
        className="shrink-0 rounded-full p-1 text-secondary transition-colors hover:bg-border/60 hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
